import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, RotateCcw, ArrowRight, ArrowDown } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { track } from "../../lib/analytics";

const QUESTIONS = [
  { id: "where", label: "Where is AI currently used?", options: ["Customer-facing", "Employee-facing", "Decision support", "Contact centre", "AI agents", "Not yet in use"] },
  { id: "customer_info", label: "Does AI process customer information?", options: ["No", "Some", "Extensively"] },
  { id: "external", label: "Can AI communicate externally?", options: ["No", "Yes"] },
  { id: "recommend", label: "Can AI make recommendations?", options: ["No", "Informational only", "Yes"] },
  { id: "internal", label: "Can AI access internal systems?", options: ["No", "Read-only", "Read and write"] },
  { id: "actions", label: "Can AI take actions?", options: ["No", "Suggests to a human", "Executes autonomously"] },
  { id: "review", label: "Which interactions require human review?", options: ["None defined", "High-risk only", "Defined by risk level"] },
  { id: "incidents", label: "How are incidents currently recorded?", options: ["Not recorded", "Ad-hoc / logs", "Structured records"] },
  { id: "outcomes", label: "How are customer outcomes monitored?", options: ["Not monitored", "Manual sampling", "Structured monitoring"] },
];

const POINTS = [
  { id: "input", name: "Input", note: "Inspect prompts, uploads and requests entering AI" },
  { id: "data", name: "Data", note: "Detect and act on sensitive customer information" },
  { id: "output", name: "Output", note: "Inspect responses before they reach the customer or workflow" },
  { id: "action", name: "Action", note: "Evaluate and hold designated actions for authorisation" },
  { id: "review", name: "Human review", note: "Route material interactions to accountable people" },
  { id: "evidence", name: "Evidence", note: "Record event, policy, action, review and outcome" },
];

export default function ControlMapper() {
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(false);
  const answered = Object.keys(answers).length;

  const generate = () => {
    setResult(true);
    track("fs_control_mapper_generated", { answered });
    setTimeout(() => document.getElementById("fs-mapper-result")?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
  };
  const reset = () => { setAnswers({}); setResult(false); };

  const current = [
    answers.where && answers.where !== "Not yet in use" ? `AI used: ${answers.where.toLowerCase()}` : "AI use being scoped",
    answers.customer_info && answers.customer_info !== "No" ? "Customer information involved" : "Limited customer information",
    answers.actions && answers.actions !== "No" ? "AI can influence or take actions" : "AI provides responses only",
    answers.review && answers.review !== "None defined" ? "Some human review defined" : "Human review not yet defined",
    answers.incidents === "Structured records" ? "Structured incident records" : "Incidents not consistently recorded",
  ];

  return (
    <div data-testid="fs-control-mapper">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {QUESTIONS.map((q) => (
          <div key={q.id} className="rounded-xl border border-white/10 bg-navy-900/60 p-4">
            <label className="block text-sm font-medium text-foreground">{q.label}</label>
            <Select value={answers[q.id] || ""} onValueChange={(v) => setAnswers((s) => ({ ...s, [q.id]: v }))}>
              <SelectTrigger className="mt-2.5 bg-navy-800 border-white/12" data-testid={`fs-mapper-${q.id}`}><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent className="bg-navy-800 border-white/12">{q.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        ))}
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button onClick={generate} disabled={answered === 0} data-testid="fs-mapper-generate" className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
          <Sparkles className="h-4 w-4" /> Map my control environment
        </button>
        {(result || answered > 0) && (
          <button onClick={reset} className="inline-flex items-center gap-2 rounded-xl border border-white/15 px-4 py-3 text-sm text-muted-foreground hover:text-foreground hover:bg-white/5"><RotateCcw className="h-4 w-4" /> Reset</button>
        )}
      </div>

      {result && (
        <div id="fs-mapper-result" className="mt-8 scroll-mt-24 grid lg:grid-cols-2 gap-4">
          <div className="rounded-lg border border-white/10 bg-navy-900/60 p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Current AI journey</p>
            <div className="mt-4 space-y-1.5">
              {current.map((c, i) => (
                <div key={c}>
                  <div className="rounded-lg border border-white/10 bg-navy-800/50 px-3.5 py-2.5 text-sm text-foreground/85">{c}</div>
                  {i < current.length - 1 && <div className="flex justify-center py-0.5"><ArrowDown className="h-3 w-3 text-muted-foreground/40" /></div>}
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-lg border border-primary/25 bg-primary/[0.04] p-6">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">Illustrative norse3 control journey</p>
            <div className="mt-4 space-y-2">
              {POINTS.map((p) => (
                <div key={p.id} className="rounded-lg border border-white/10 bg-navy-800/50 px-3.5 py-2.5">
                  <p className="text-sm font-semibold text-foreground">{p.name}</p>
                  <p className="text-xs text-muted-foreground">{p.note}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-2">
            <p className="text-xs text-muted-foreground">This is an illustrative architecture, not a compliance assessment.</p>
            <Link to="/financial-services#walkthrough" onClick={() => track("fs_mapper_cta")} data-testid="fs-mapper-cta" className="mt-3 inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90">
              Discuss your environment <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
